import { useIsOnline, useSyncManagerState } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import OnlineIcon from './icons/online.svg';
import OfflineIcon from './icons/offline.svg';
import CompletedIcon from './icons/completed.svg';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import { Icon } from '@material-ui/core';
import { db, SyncError, SyncManager } from 'db';
import { useGetOfflineFirstSectionsQuery } from 'services/api/vehicle-sections';
import { useFormVehicleId } from '../hooks/useFormVehicleId';
import { Wrapper, CloseIcon } from './ui';
import { sendClientLog } from 'services/api/client-logs';

const OFFLINE_BG = '#E93323';
const ONLINE_BG = '#479747';
const COMPLETED_BG = '#3994DE';
const ERROR_BG = '#D32F2F';

const SYNC_FAILURE_THRESHOLD = 3;
const STALE_CHECK_INTERVAL = 30_000; // 30s
const STALE_DATA_THRESHOLD = 60_000; // 1min with no successful sync = stale

const typeMap = {
  online: {
    bg: ONLINE_BG,
    text: 'Back online, sync in process',
    Icon: OnlineIcon
  },
  offline: {
    bg: OFFLINE_BG,
    text: "You're offline but you can keep using Autoaxess. We'll sync your data once you're back online.",
    Icon: OfflineIcon
  },
  completed: {
    bg: COMPLETED_BG,
    text: 'Sync completed',
    Icon: CompletedIcon
  },
  syncError: {
    bg: ERROR_BG,
    text: 'We were unable to save your latest changes. Please refresh the page and try again.',
    Icon: ErrorIcon
  },
  dbError: {
    bg: ERROR_BG,
    text: 'There was a problem accessing local storage. Please reload the page to continue.',
    Icon: ErrorIcon
  }
};

const wait = () =>
  new Promise<void>(res =>
    setTimeout(() => {
      res();
    }, 5000)
  );

export const Banner = () => {
  const [visibleType, setVisibleType] = useState<'offline' | 'online' | 'completed' | 'syncError' | 'dbError' | null>(
    null
  );

  let timeout: number;

  const [uiSsSyncing, setUiIsSyncing] = useState(false);

  const closeBanner = () => setVisibleType(null);

  const vehicleId = useFormVehicleId();

  const { refetch } = useGetOfflineFirstSectionsQuery(Number(vehicleId), { skip: !vehicleId });

  const syncManager = SyncManager.getInstance(vehicleId);
  const { consecutiveFailures, lastError } = useSyncManagerState(syncManager);

  const prevOffline = useRef<boolean | null>(null);
  const isOnline = useIsOnline();

  const timeoutRef = useRef<number | null>(null);
  const lastLoggedErrorRef = useRef<number | null>(null);

  const performSync = async () => {
    setVisibleType('online');
    setUiIsSyncing(true);

    await Promise.all([syncManager.start(), wait()]);
    await refetch();
    setVisibleType('completed');

    timeoutRef.current = setTimeout(() => {
      setUiIsSyncing(false);
      setVisibleType(null);
    }, 3000);
  };

  // Wire up server-side error logging
  useEffect(() => {
    if (!vehicleId) return;
    const sm = SyncManager.getInstance(vehicleId);

    sm.onSyncError = (error: SyncError) => {
      // Avoid logging the same error repeatedly - log on 1st, 3rd, and every 5th failure
      const f = error.consecutiveFailures;
      if (f === 1 || f === SYNC_FAILURE_THRESHOLD || f % 5 === 0) {
        sendClientLog({
          level: 'error',
          context: 'sync-manager',
          message: `Sync failed (attempt ${f}): ${error.message}`,
          metadata: {
            vehicleId: error.vehicleId,
            status: error.status,
            consecutiveFailures: f,
            timestamp: error.timestamp,
            userAgent: navigator.userAgent
          }
        });
      }
    };
  }, [vehicleId]);

  // Show sync error banner after threshold consecutive failures
  useEffect(() => {
    if (consecutiveFailures >= SYNC_FAILURE_THRESHOLD && isOnline) {
      setVisibleType('syncError');
    } else if (consecutiveFailures === 0 && visibleType === 'syncError') {
      // Sync recovered - clear error banner
      setVisibleType(null);
    }
  }, [consecutiveFailures, isOnline]);

  // Watchdog: detect unsynced data sitting idle (sync never fires or DB wiped mid-flow)
  useEffect(() => {
    if (!vehicleId || !isOnline) return;

    const staleCheckRef = { logged: false };
    const interval = setInterval(async () => {
      const sm = SyncManager.getInstance(vehicleId);

      // If sync is actively running or queued, nothing is stale
      if (sm.isBusy || sm.isPaused) return;

      try {
        const counts = await sm.getUnsyncedCounts();
        const total = counts.entityUpdates + counts.vehicleUpdates + counts.removedImages + counts.videoUploads;

        if (total > 0 && !staleCheckRef.logged) {
          // Unsynced records exist but SyncManager is idle and online — something is wrong
          staleCheckRef.logged = true;

          sendClientLog({
            level: 'warn',
            context: 'sync-watchdog',
            message: `Stale unsynced data detected: ${total} records idle while online`,
            metadata: {
              vehicleId,
              ...counts,
              isStopped: sm.isStopped,
              isPaused: sm.isPaused,
              consecutiveFailures: sm.consecutiveFailures,
              userAgent: navigator.userAgent
            }
          });

          // Try to kick sync back into action
          sm.scheduleDebouncedSync();
        } else if (total === 0) {
          staleCheckRef.logged = false;
        }
      } catch (err) {
        // DB might be deleted/unavailable — that's itself diagnostic
        if (!staleCheckRef.logged) {
          staleCheckRef.logged = true;
          setVisibleType('dbError');
          sendClientLog({
            level: 'error',
            context: 'sync-watchdog',
            message: 'Failed to query IndexedDB for unsynced records',
            metadata: {
              vehicleId,
              error: String(err),
              userAgent: navigator.userAgent
            }
          });
        }
      }
    }, STALE_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [vehicleId, isOnline]);

  useEffect(() => {
    if (isOnline && prevOffline.current === false) {
      console.log('use effect double trigger???');
      performSync();
    }

    if (!isOnline) {
      syncManager.pause();
      setVisibleType('offline');
    }

    prevOffline.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    console.log('delete runs');
    db.delete({ disableAutoOpen: false });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (visibleType === null) return null;

  const { bg, Icon: BannerIcon, text } = typeMap[visibleType];

  return (
    <Wrapper bg={bg}>
      <BannerIcon />
      <span>{text}</span>
      <CloseIcon onClick={closeBanner}>close</CloseIcon>
    </Wrapper>
  );
};
